import type { ReactElement } from "react";
import type { TileKey, TileRow, TileSection } from "@kundeninfo/api-client";
import { Listenkachel } from "@kundeninfo/ui/me-design";
import type { ListenkachelKennzahl } from "@kundeninfo/ui/me-design";
import { COV_TEXT, TILE_TEXT, companyOf } from "./texts.js";
import { TILE_CATALOG, tileId } from "./tileCatalog.js";
import type { ServerTileKey, TileContext, TileMapping, TileRowView } from "./tileCatalog.js";

/** Eine Zeile der Kachel: Anzeigedaten, die Kürzel der Unternehmen (JSON, wenn der Kontext sie verlangt) und was ein Klick auslöst. */
export type FrameRow = { id: string; view: TileRowView; tags?: string | undefined; onSelect: () => void };

/** Was eine Kachel zeigt: Zähler, Leertext, Kennzahlen (nur mit Zeilen, E448), Zeilen und, wenn Einträge fehlen, „weitere“. */
export type TileBody = {
  count: number; empty: string; facts: ListenkachelKennzahl[]; rows: readonly FrameRow[]; more?: { count: number; text: string };
};

export type TileFrameProps = {
  tileKey: TileKey; hidden: boolean;
  /** Kopf und „weitere“ führen beide in den Reiter der Kachel. */
  onHead: () => void;
  onRetry: () => void;
  /** Der Inhalt, oder der Text des Ladefehlers (dann nur Kopf und „Erneut laden“). */
  content: TileBody | { error: string };
};

/** Die `data-*`-Angaben einer Zeile für die Listenkachel; was fehlt, bleibt weg. */
function rowData({ view, tags }: FrameRow): Record<string, string | undefined> {
  const attributes = { "data-kennung": view.kennung, "data-zusatz": view.zusatz, "data-unter": view.unter,
    "data-status-art": view.statusArt, "data-status": view.status, "data-kennzeichen": tags };
  return Object.fromEntries(Object.entries(attributes).filter(([, value]) => value));
}

/** Der einzige Weg, auf dem die Übersicht eine Kachel zeichnet: Titel, Symbol und Kennung aus Katalog und Texttabelle, der Rest vom Aufrufer. */
export function TileFrame(p: TileFrameProps): ReactElement {
  const common = { id: tileId(p.tileKey), titel: TILE_TEXT[p.tileKey].title, art: TILE_CATALOG[p.tileKey].art,
    wert: p.tileKey, ausgeblendet: p.hidden, onGewaehlt: () => p.onHead() };
  if ("error" in p.content) return <Listenkachel {...common} fehler={p.content.error} onErneut={() => p.onRetry()} />;
  const { count, empty, facts, rows, more } = p.content;
  // Entscheid 448: Ohne Zeilen steht nur der Leertext da; „überfällig 0 · offen –“ über „Keine offenen Tickets.“ sagt nichts.
  return (
    <Listenkachel {...common} zaehler={count} leer={empty} kennzahlen={rows.length === 0 ? [] : facts}
      {...(more === undefined ? {} : { weitere: more.count, weitereText: more.text })}
      onZeileGewaehlt={(d) => rows[d.index]?.onSelect()}>
      {rows.map((r) => <option key={r.id} value={r.id} {...rowData(r)}>{r.view.text}</option>)}
    </Listenkachel>
  );
}

export type TileViewProps = {
  tileKey: ServerTileKey; section: TileSection; mapping: TileMapping; ctx: TileContext; hidden: boolean;
  onHead: () => void;
  onRow: (row: TileRow) => void;
  onRetry: () => void;
};

/** Die Kürzel der Unternehmen einer Zeile; nur, wenn der Kontext sie verlangt. */
function companyTags(row: TileRow, ctx: TileContext): string | undefined {
  return ctx.showCompanyCodes && row.companyIds.length > 0
    ? JSON.stringify(row.companyIds.map((id) => companyOf(id, ctx.companies))
      .map((c) => ({ kuerzel: c.code, name: c.name, ...(c.color ? { farbe: c.color } : {}) })))
    : undefined;
}

/** Ein Abschnitt vom Server als Kachel: Kopf mit Zähler, Kennzahlen, höchstens die Zeilen vom Server, „weitere“. */
export function TileView(p: TileViewProps): ReactElement {
  const base = { tileKey: p.tileKey, hidden: p.hidden, onHead: p.onHead, onRetry: p.onRetry };
  if (p.section.status === "ERROR") return <TileFrame {...base} content={{ error: COV_TEXT.tileFailed }} />;
  const { rows, total, facts } = p.section;
  // Unsichtbare Konzernmitglieder zählen als nicht gezeigte Einträge („1 nicht einsehbar“ steht in „weitere“).
  const hiddenMembers = facts.hiddenMembers ?? 0;
  const more = total - rows.length + hiddenMembers;
  return <TileFrame {...base} content={{ count: total, empty: TILE_TEXT[p.tileKey].empty(hiddenMembers),
    facts: p.mapping.facts(facts, p.ctx),
    rows: rows.map((r) => ({ id: r.id, view: p.mapping.row(r, p.ctx), tags: companyTags(r, p.ctx), onSelect: () => p.onRow(r) })),
    ...(more > 0 ? { more: { count: more, text: TILE_TEXT[p.tileKey].more(total, hiddenMembers) } } : {}) }} />;
}
